import { ChangeDetectionStrategy, Component, effect, inject, Signal, viewChildren } from '@angular/core';

import { toastAnimation } from './toast-container.animations';
import { ToastComponent } from './toast.component';
import { InternalToast } from './toast.model';
import { ToastService } from './toast.service';

@Component({
  selector: 'sb-toast-container',
  standalone: true,
  imports: [ToastComponent],
  templateUrl: './toast-container.component.html',
  styleUrl: './toast-container.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[class.has-multiple]': 'toasts().length > 1',
    '(mouseenter)': 'pauseAll()',
    '(mouseleave)': 'unPauseAll()',
  },
  animations: [toastAnimation],
})
export class ToastContainerComponent {
  private readonly toastComponents = viewChildren(ToastComponent);
  private readonly toastService = inject(ToastService);
  public readonly toasts: Signal<InternalToast[]> = this.toastService.visibleToasts;

  constructor() {
    effect(() => {
      this.styleToastStack();
    });
  }

  pauseAll(): void {
    this.toastService.pauseAll();
  }

  unPauseAll(): void {
    this.toastService.unPauseAll();
  }

  private styleToastStack(): void {
    const components = this.toastComponents();
    if (!components.length) {
      return;
    }

    const toasts = Array.from(components).reverse();
    let cumulativeHeight = 0;

    toasts.forEach((toast, index) => {
      const sortIndex = index + 1;
      const el = toast.elementRef.nativeElement;
      const height = el.clientHeight;

      this.setToastStyles(el, sortIndex, cumulativeHeight);

      cumulativeHeight += height;
    });
  }

  private setToastStyles(element: HTMLElement, sortIndex: number, cumulativeHeight: number): void {
    element.style.setProperty('--index', `${sortIndex}`);

    if (sortIndex > 1) {
      element.style.setProperty('--hover-offset-y', `-${cumulativeHeight}px`);
    } else {
      element.style.removeProperty('--hover-offset-y');
    }
  }
}
